import React, { useState, useEffect, useRef } from 'react';
import {
  IconUser, IconBriefcase, IconPhone, IconPlayerStop, IconClock,
  IconCheck, IconRefresh, IconChartArrowsVertical, IconHeadset,
  IconSpeakerphone, IconDeviceMobile, IconLoader2
} from '@tabler/icons-react';
import { AGENTS, INDUSTRIES } from '../data/industries';
import { whitelistNumber, blacklistNumber, triggerAgentMessage, sendCampaignToNumber } from '../api/whitelist';

const AGENT_ICONS = { abdullah: IconChartArrowsVertical, khalid: IconHeadset, latifa: IconSpeakerphone };

// ─── Industry-specific mock conversations ───
function getCustomerMessages(industryId, lang) {
  // Pull conversation from industry data directly
  const allMsgs = {
    real_estate: {
      ar: [
        { from: 'agent', agent: 'abdullah', text: 'أهلاً وسهلاً! أنا عايدة. كيف أقدر أساعدك اليوم؟' },
        { from: 'customer', text: 'السلام عليكم، أبي شقة في النرجس ٣ غرف' },
        { from: 'agent', agent: 'abdullah', text: 'وعليكم السلام! عندنا ٣ وحدات بالنرجس تناسب طلبك. الأسعار تبدأ من ١.٢ مليون. تبي أرسلك التفاصيل؟' },
        { from: 'customer', text: 'ايه أرسلها. وكم المساحة؟' },
        { from: 'agent', agent: 'abdullah', text: 'المساحات من ١٤٠ إلى ١٨٥ متر مربع. أرسلتلك المخططات والصور. تبي نحجز موعد معاينة؟' },
        { from: 'customer', text: 'ايه يوم الأحد الصبح لو ممكن' },
        { from: 'agent', agent: 'abdullah', text: 'تمام! سجلتلك موعد يوم الأحد الساعة ١٠ الصبح. بأرسلك الموقع والتفاصيل قبل الموعد.' },
        { from: 'system', text: 'تم إنشاء صفقة — المرحلة: مؤهل | القيمة: ١.٥ مليون ريال\nتم حجز موعد معاينة — الأحد ١٠:٠٠ ص' },
      ],
      en: [
        { from: 'agent', agent: 'abdullah', text: "Welcome! I'm AIDA. How can I help you today?" },
        { from: 'customer', text: 'Hi, I want a 3-bedroom apartment in Narjis' },
        { from: 'agent', agent: 'abdullah', text: 'We have 3 units in Narjis that match. Prices start from 1.2M SAR. Want me to send details?' },
        { from: 'customer', text: 'Yes send them. What are the sizes?' },
        { from: 'agent', agent: 'abdullah', text: "Sizes range from 140 to 185 sqm. I've sent floor plans and photos. Want to schedule a viewing?" },
        { from: 'customer', text: 'Yes, Sunday morning if possible' },
        { from: 'agent', agent: 'abdullah', text: "Done! Booked Sunday at 10 AM. I'll send location and details before the appointment." },
        { from: 'system', text: 'Deal created — Stage: Qualified | Value: 1.5M SAR\nViewing booked — Sunday 10:00 AM' },
      ],
    },
    cleaning: {
      ar: [
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! أنا عايدة. كيف أقدر أساعدك؟' },
        { from: 'customer', text: 'أبي تنظيف شقة ٣ غرف في الملقا' },
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! عندنا باقتين: التنظيف الأساسي (٣٥٠ ريال) والتنظيف العميق (٥٥٠ ريال). الفرق إن العميق يشمل المكيفات والمطبخ بالتفصيل.' },
        { from: 'customer', text: 'العميق. متى أقرب موعد؟' },
        { from: 'agent', agent: 'abdullah', text: 'عندنا موعد بكرة الصبح من ٨ إلى ١٢. يناسبك؟' },
        { from: 'customer', text: 'تمام احجزه' },
        { from: 'agent', agent: 'abdullah', text: 'تم الحجز! فريق من ٣ أشخاص بيكون عندك بكرة الساعة ٨. بأرسلك رسالة تأكيد مع تفاصيل الفريق.' },
        { from: 'system', text: 'حجز مؤكد — تنظيف عميق | ٥٥٠ ريال\nبكرة ٨:٠٠ ص — ٣ أفراد' },
      ],
      en: [
        { from: 'agent', agent: 'abdullah', text: "Hi! I'm AIDA. How can I help?" },
        { from: 'customer', text: 'I need cleaning for a 3-bedroom apartment in Malqa' },
        { from: 'agent', agent: 'abdullah', text: 'We have two packages: Standard Clean (350 SAR) and Deep Clean (550 SAR). Deep includes AC and detailed kitchen.' },
        { from: 'customer', text: 'Deep clean. When is the earliest?' },
        { from: 'agent', agent: 'abdullah', text: 'We have tomorrow morning 8 AM - 12 PM. Works for you?' },
        { from: 'customer', text: 'Book it' },
        { from: 'agent', agent: 'abdullah', text: "Booked! A 3-person team will arrive at 8 AM. I'll send confirmation with team details." },
        { from: 'system', text: 'Booking confirmed — Deep Clean | 550 SAR\nTomorrow 8:00 AM — 3 crew members' },
      ],
    },
    car_dealership: {
      ar: [
        { from: 'agent', agent: 'abdullah', text: 'أهلاً وسهلاً! أنا عايدة. تبي تشوف سياراتنا الجديدة؟' },
        { from: 'customer', text: 'أبي كامري ٢٠٢٦ فل كامل. عندكم؟' },
        { from: 'agent', agent: 'abdullah', text: 'عندنا ٤ كامري ٢٠٢٦ فل كامل. الألوان: أبيض، أسود، فضي، وأزرق. السعر يبدأ من ١٣٥ ألف.' },
        { from: 'customer', text: 'الأبيض. كم الأقساط؟' },
        { from: 'agent', agent: 'abdullah', text: 'الأبيض متوفر. بدون دفعة أولى: ٢,٨٠٠ ريال/شهر لمدة ٥ سنوات. مع دفعة ٢٠٪: ٢,٢٤٠ ريال/شهر. تبي تجي تجربها؟' },
        { from: 'customer', text: 'ايه بجي بكرة' },
        { from: 'agent', agent: 'abdullah', text: 'حجزتلك موعد تجربة بكرة الساعة ٤ العصر. السيارة بتكون جاهزة لك. بأرسلك الموقع.' },
        { from: 'system', text: 'صفقة جديدة — كامري ٢٠٢٦ فل | ١٣٥,٠٠٠ ريال\nموعد تجربة قيادة — بكرة ٤:٠٠ م' },
      ],
      en: [
        { from: 'agent', agent: 'abdullah', text: "Welcome! I'm AIDA. Looking for a new car?" },
        { from: 'customer', text: 'I want a 2026 Camry fully loaded. Do you have it?' },
        { from: 'agent', agent: 'abdullah', text: 'We have 4 fully loaded 2026 Camry units. Colors: white, black, silver, blue. Starting from 135K SAR.' },
        { from: 'customer', text: 'White. What are the installments?' },
        { from: 'agent', agent: 'abdullah', text: 'White is available. No down payment: 2,800/month for 5 years. With 20% down: 2,240/month. Want to schedule a test drive?' },
        { from: 'customer', text: "Yes I'll come tomorrow" },
        { from: 'agent', agent: 'abdullah', text: "Test drive booked for tomorrow 4 PM. Car will be ready. I'll send the location." },
        { from: 'system', text: 'New deal — 2026 Camry Fully Loaded | 135,000 SAR\nTest drive — Tomorrow 4:00 PM' },
      ],
    },
    insurance: {
      ar: [
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! أنا عايدة. كيف أقدر أساعدك؟' },
        { from: 'customer', text: 'أبي تأمين شامل لسيارتي كامري ٢٠٢٤' },
        { from: 'agent', agent: 'abdullah', text: 'التأمين الشامل للكامري ٢٠٢٤ يبدأ من ٣,٨٠٠ ريال سنوياً. يشمل: حوادث، سرقة، كوارث طبيعية، وطرف ثالث. تبي عرض مفصّل؟' },
        { from: 'customer', text: 'ايه ارسلي العرض' },
        { from: 'agent', agent: 'abdullah', text: 'أرسلتلك العرض. صالح لمدة ٧ أيام. تبي أحجزلك موعد مع المستشار لشرح التغطيات بالتفصيل؟' },
        { from: 'customer', text: 'ايه احجز' },
        { from: 'agent', agent: 'abdullah', text: 'تم! حجزتلك بكرة الساعة ١١ الصبح مع المستشار أحمد. بأرسلك التأكيد.' },
        { from: 'system', text: 'صفقة جديدة — تأمين شامل كامري | ٣,٨٠٠ ر.س\nموعد استشارة — بكرة ١١:٠٠ ص' },
      ],
      en: [
        { from: 'agent', agent: 'abdullah', text: "Hi! I'm AIDA. How can I help?" },
        { from: 'customer', text: 'I need comprehensive insurance for my 2024 Camry' },
        { from: 'agent', agent: 'abdullah', text: 'Comprehensive for a 2024 Camry starts from 3,800 SAR/year. Covers: accidents, theft, natural disasters, third-party. Want a detailed quote?' },
        { from: 'customer', text: 'Yes send the quote' },
        { from: 'agent', agent: 'abdullah', text: "Sent! Valid for 7 days. Want to book an appointment with a consultant for detailed coverage explanation?" },
        { from: 'customer', text: 'Yes book it' },
        { from: 'agent', agent: 'abdullah', text: "Done! Booked tomorrow 11 AM with consultant Ahmad. I'll send confirmation." },
        { from: 'system', text: 'New deal — Comprehensive Camry | 3,800 SAR\nConsultation — Tomorrow 11:00 AM' },
      ],
    },
    retail: {
      ar: [
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! أنا عايدة. كيف أقدر أساعدك اليوم؟' },
        { from: 'customer', text: 'عندكم العطر الجديد من توم فورد؟' },
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! ايه متوفر — Tobacco Vanille و Oud Wood. تبين توصيل ولا تمرين على الفرع؟' },
        { from: 'customer', text: 'توصيل لو سمحت. كم سعر Oud Wood؟' },
        { from: 'agent', agent: 'abdullah', text: 'Oud Wood ١,٢٠٠ ريال. التوصيل مجاني للطلبات فوق ٥٠٠ ريال. تبين أكمّل الطلب؟' },
        { from: 'customer', text: 'ايه أكمله' },
        { from: 'agent', agent: 'abdullah', text: 'تم! رقم طلبك #٤٥٦٧. التوصيل بكرة بين ٢-٦ م. بأرسلك رابط التتبع.' },
        { from: 'system', text: 'طلب جديد #٤٥٦٧ — Oud Wood | ١,٢٠٠ ر.س\nتوصيل — بكرة ٢:٠٠-٦:٠٠ م' },
      ],
      en: [
        { from: 'agent', agent: 'abdullah', text: "Hi! I'm AIDA. How can I help today?" },
        { from: 'customer', text: 'Do you have the new Tom Ford fragrance?' },
        { from: 'agent', agent: 'abdullah', text: 'Yes! Tobacco Vanille and Oud Wood are available. Delivery or store visit?' },
        { from: 'customer', text: 'Delivery please. How much is Oud Wood?' },
        { from: 'agent', agent: 'abdullah', text: 'Oud Wood is 1,200 SAR. Free delivery for orders over 500 SAR. Want me to complete the order?' },
        { from: 'customer', text: 'Yes complete it' },
        { from: 'agent', agent: 'abdullah', text: "Done! Order #4567. Delivery tomorrow 2-6 PM. I'll send the tracking link." },
        { from: 'system', text: 'New order #4567 — Oud Wood | 1,200 SAR\nDelivery — Tomorrow 2:00-6:00 PM' },
      ],
    },
    furniture: {
      ar: [
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! أنا عايدة. كيف أقدر أساعدك؟' },
        { from: 'customer', text: 'أبي كنبة على شكل L لصالة كبيرة' },
        { from: 'agent', agent: 'abdullah', text: 'عندنا ٣ موديلات L تناسب الصالات الكبيرة. الأسعار من ٨,٥٠٠ إلى ١٤,٠٠٠ ريال. تبي أرسلك الصور والمقاسات؟' },
        { from: 'customer', text: 'ايه أرسلها. وعندكم تقسيط؟' },
        { from: 'agent', agent: 'abdullah', text: 'أرسلتلك الكتالوج. ايه عندنا تقسيط ٤ أشهر بدون فوائد على تمارا. تبي تجي تشوفها بالمعرض؟' },
        { from: 'customer', text: 'ايه متى عندكم فتح؟' },
        { from: 'agent', agent: 'abdullah', text: 'المعرض مفتوح من ١٠ الصبح لـ١٠ الليل. حجزتلك موعد بكرة الساعة ٥ عصراً مع مستشار التصميم.' },
        { from: 'system', text: 'صفقة جديدة — كنبة L | القيمة: ١٢,٠٠٠ ر.س\nزيارة معرض — بكرة ٥:٠٠ م' },
      ],
      en: [
        { from: 'agent', agent: 'abdullah', text: "Hi! I'm AIDA. How can I help?" },
        { from: 'customer', text: 'I need an L-shaped sofa for a large living room' },
        { from: 'agent', agent: 'abdullah', text: 'We have 3 L-shaped models for large rooms. Prices from 8,500 to 14,000 SAR. Want me to send photos and sizes?' },
        { from: 'customer', text: 'Yes send them. Do you have installments?' },
        { from: 'agent', agent: 'abdullah', text: "Sent the catalog. Yes, we have 4-month interest-free installments via Tamara. Want to visit the showroom?" },
        { from: 'customer', text: 'Yes, when are you open?' },
        { from: 'agent', agent: 'abdullah', text: "Showroom is open 10 AM to 10 PM. Booked you tomorrow at 5 PM with our design consultant." },
        { from: 'system', text: 'New deal — L-shaped Sofa | Value: 12,000 SAR\nShowroom visit — Tomorrow 5:00 PM' },
      ],
    },
    fnb: {
      ar: [
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! أنا عايدة. كيف أقدر أساعدك اليوم؟' },
        { from: 'customer', text: 'أبي أطلب تموين لمناسبة ٥٠ شخص' },
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! عندنا ٣ باقات تموين:\n- الفضية: ٧٥ ريال/شخص\n- الذهبية: ١٢٠ ريال/شخص\n- البلاتينية: ١٨٠ ريال/شخص\nايش نوع المناسبة؟' },
        { from: 'customer', text: 'عزيمة عائلية. الذهبية تمام' },
        { from: 'agent', agent: 'abdullah', text: 'الذهبية لـ٥٠ شخص = ٦,٠٠٠ ريال. تشمل: ٣ أطباق رئيسية، مقبلات، حلويات، ومشروبات. متى المناسبة؟' },
        { from: 'customer', text: 'الجمعة الجاية الساعة ٧ المغرب' },
        { from: 'agent', agent: 'abdullah', text: 'تم! حجزتلك الجمعة ٧ م. الفريق بيكون عندك ٦:٣٠ للتجهيز. بأرسلك القائمة والتأكيد.' },
        { from: 'system', text: 'طلب تموين — باقة ذهبية | ٥٠ شخص | ٦,٠٠٠ ر.س\nالجمعة ٧:٠٠ م' },
      ],
      en: [
        { from: 'agent', agent: 'abdullah', text: "Hi! I'm AIDA. How can I help today?" },
        { from: 'customer', text: 'I need catering for 50 people' },
        { from: 'agent', agent: 'abdullah', text: "We have 3 packages:\n- Silver: 75 SAR/person\n- Gold: 120 SAR/person\n- Platinum: 180 SAR/person\nWhat's the occasion?" },
        { from: 'customer', text: 'Family gathering. Gold is fine' },
        { from: 'agent', agent: 'abdullah', text: 'Gold for 50 = 6,000 SAR. Includes: 3 main dishes, appetizers, desserts, and drinks. When is the event?' },
        { from: 'customer', text: 'Next Friday 7 PM' },
        { from: 'agent', agent: 'abdullah', text: "Booked! Friday 7 PM. Team arrives 6:30 for setup. I'll send the menu and confirmation." },
        { from: 'system', text: 'Catering order — Gold Package | 50 guests | 6,000 SAR\nFriday 7:00 PM' },
      ],
    },
    physio: {
      ar: [
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! أنا عايدة من العيادة. كيف أقدر أساعدك؟' },
        { from: 'customer', text: 'عندي ألم أسفل الظهر من فترة' },
        { from: 'agent', agent: 'abdullah', text: 'أسمع ذلك كثير. عندنا برنامج متخصص لآلام الظهر — يبدأ بتقييم شامل (٣٠ دقيقة) ثم خطة علاج مخصصة. التقييم الأولي ٢٥٠ ريال. تبي نحجز؟' },
        { from: 'customer', text: 'ايه متى عندكم أقرب موعد؟' },
        { from: 'agent', agent: 'abdullah', text: 'عندنا بكرة الساعة ١١ الصبح مع الدكتور فهد، متخصص في آلام الظهر والعمود الفقري. يناسبك؟' },
        { from: 'customer', text: 'تمام' },
        { from: 'agent', agent: 'abdullah', text: 'تم الحجز! بأرسلك الموقع واستبيان سريع تعبيه قبل الموعد عشان الدكتور يكون جاهز.' },
        { from: 'system', text: 'موعد تقييم — آلام أسفل الظهر | ٢٥٠ ر.س\nبكرة ١١:٠٠ ص — د. فهد' },
      ],
      en: [
        { from: 'agent', agent: 'abdullah', text: "Hi! I'm AIDA. How can I help?" },
        { from: 'customer', text: "I've had lower back pain for a while" },
        { from: 'agent', agent: 'abdullah', text: 'We hear that often. We have a specialized back pain program — starts with a full assessment (30 min) then a customized treatment plan. Initial assessment is 250 SAR. Want to book?' },
        { from: 'customer', text: 'Yes, when is the earliest appointment?' },
        { from: 'agent', agent: 'abdullah', text: 'Tomorrow 11 AM with Dr. Fahad, spine and back pain specialist. Works for you?' },
        { from: 'customer', text: 'Perfect' },
        { from: 'agent', agent: 'abdullah', text: "Booked! I'll send the location and a quick questionnaire to fill before the appointment so the doctor is prepared." },
        { from: 'system', text: 'Assessment appointment — Lower back pain | 250 SAR\nTomorrow 11:00 AM — Dr. Fahad' },
      ],
    },
    derma: {
      ar: [
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! أنا عايدة من العيادة. كيف أقدر أساعدك؟' },
        { from: 'customer', text: 'أبي استشارة عن إزالة التصبغات بالليزر' },
        { from: 'agent', agent: 'abdullah', text: 'عندنا عدة خيارات لإزالة التصبغات:\n- ليزر Q-Switch: ٨٠٠ ريال/جلسة\n- IPL: ٦٠٠ ريال/جلسة\n- باقة ٤ جلسات: خصم ٢٠٪\nتبين نحجز استشارة مجانية أول؟' },
        { from: 'customer', text: 'ايه أبي استشارة أول' },
        { from: 'agent', agent: 'abdullah', text: 'عندنا بكرة الساعة ٢ الظهر مع الدكتورة نورة، متخصصة في الليزر والتجميل. يناسبك؟' },
        { from: 'customer', text: 'تمام' },
        { from: 'agent', agent: 'abdullah', text: 'تم! حجزتلك استشارة مجانية بكرة ٢ م. بأرسلك الموقع وتعليمات التحضير.' },
        { from: 'system', text: 'استشارة مجانية — إزالة تصبغات بالليزر\nبكرة ٢:٠٠ م — د. نورة' },
      ],
      en: [
        { from: 'agent', agent: 'abdullah', text: "Hi! I'm AIDA. How can I help?" },
        { from: 'customer', text: 'I want a consultation about laser pigmentation removal' },
        { from: 'agent', agent: 'abdullah', text: "We have several options:\n- Q-Switch Laser: 800 SAR/session\n- IPL: 600 SAR/session\n- 4-session package: 20% discount\nWant to book a free consultation first?" },
        { from: 'customer', text: 'Yes, consultation first' },
        { from: 'agent', agent: 'abdullah', text: 'Tomorrow 2 PM with Dr. Noura, laser and cosmetics specialist. Works for you?' },
        { from: 'customer', text: 'Perfect' },
        { from: 'agent', agent: 'abdullah', text: "Booked! Free consultation tomorrow 2 PM. I'll send location and preparation instructions." },
        { from: 'system', text: 'Free consultation — Laser pigmentation removal\nTomorrow 2:00 PM — Dr. Noura' },
      ],
    },
    saas: {
      ar: [
        { from: 'agent', agent: 'abdullah', text: 'أهلاً! أنا عايدة. كيف أقدر أساعدك؟' },
        { from: 'customer', text: 'عندنا سلسلة مطاعم ١٢ فرع ونبي نغير أجهزة الـPOS. ايش تعرضون؟' },
        { from: 'agent', agent: 'abdullah', text: 'لـ١٢ فرع باقة Business:\n• Smart POS بشريحة 4G\n• mada + Visa + Apple Pay + STC Pay\n• Settlement يومي T+١\n• تكامل مباشر مع Foodics\n• ١.٤٥٪ + ٧٩٩ ر.س للجهاز' },
        { from: 'customer', text: 'نبي تجربة شهر بفرعين قبل الالتزام' },
        { from: 'agent', agent: 'abdullah', text: 'أكيد! أرسل جهازين للفرع الرئيسي بكرة. التفعيل خلال ٢٤ ساعة. التركيب والتدريب مجاناً. لو الأداء عجبكم نوسّع للـ١٢ فرع.' },
        { from: 'customer', text: 'ممتاز، أرسل التفاصيل' },
        { from: 'agent', agent: 'abdullah', text: 'تم. الجهازان في الطريق + رابط التفعيل. حسابك مهيّأ مسبقاً بتكامل Foodics. أي استفسار خلال التجربة، أنا متواجد.' },
        { from: 'system', text: 'صفقة جديدة — Business × ١٢ جهاز | الإيراد السنوي: ٤٢,٦٦٠ ر.س\nتجربة مفعلة على فرعين — ٣٠ يوم' },
      ],
      en: [
        { from: 'agent', agent: 'abdullah', text: "Hi! I'm AIDA. How can I help?" },
        { from: 'customer', text: 'We have a 12-branch restaurant chain and want to switch our POS terminals. What do you offer?' },
        { from: 'agent', agent: 'abdullah', text: 'For 12 branches, Business plan:\n• Smart POS with 4G SIM\n• mada + Visa + Apple Pay + STC Pay\n• Daily T+1 settlement\n• Direct Foodics integration\n• 1.45% + 799 SAR per device' },
        { from: 'customer', text: 'We want a 1-month trial on 2 branches before committing' },
        { from: 'agent', agent: 'abdullah', text: "Of course! Sending 2 devices to your main branch tomorrow. Activation within 24h. Installation and training free. If you're happy, we roll out to all 12." },
        { from: 'customer', text: 'Perfect, send the details' },
        { from: 'agent', agent: 'abdullah', text: "Done. The 2 devices are on the way + activation link. Your account is pre-configured with Foodics integration. Any questions during the trial, I'm here." },
        { from: 'system', text: 'New deal — Business × 12 terminals | ARR: 42,660 SAR\nTrial active on 2 branches — 30 days' },
      ],
    },
  };
  // Add Latifa (marketing) and Khalid (support) phases to every industry
  const marketingMsgs = {
    real_estate: {
      ar: [
        { from: 'customer', text: 'بالمناسبة، عندكم عروض حالياً على الوحدات؟' },
        { from: 'agent', agent: 'latifa', text: 'أنا لطيفة من فريق التسويق! ايه عندنا حملة "امتلك بيتك" — خصم ٥٪ على الدفعة الأولى + تأثيث مجاني للمطبخ. العرض ينتهي نهاية الشهر.' },
        { from: 'customer', text: 'حلو! كيف أعرف عن العروض الجديدة؟' },
        { from: 'agent', agent: 'latifa', text: 'سجلتك في قائمة العروض الحصرية. بأرسلك كل عرض جديد على الواتساب مباشرة. وعندنا حملة جديدة الأسبوع الجاي على فلل الياسمين — تبي أضيفك؟' },
      ],
      en: [
        { from: 'customer', text: 'By the way, any current promotions on units?' },
        { from: 'agent', agent: 'latifa', text: "I'm Latifa from marketing! Yes, we have our \"Own Your Home\" campaign — 5% off down payment + free kitchen furnishing. Offer ends this month." },
        { from: 'customer', text: 'Nice! How do I stay updated on new offers?' },
        { from: 'agent', agent: 'latifa', text: "Added you to our exclusive offers list. I'll send new deals directly on WhatsApp. We have a new campaign next week on Yasmin villas — want me to add you?" },
      ],
    },
    cleaning: {
      ar: [
        { from: 'customer', text: 'عندكم اشتراكات شهرية؟ يمكن أوفر' },
        { from: 'agent', agent: 'latifa', text: 'أنا لطيفة من التسويق! ايه عندنا باقة شهرية — ٤ زيارات بـ١,٨٠٠ ريال بدل ٢,٢٠٠. يعني توفر ٤٠٠ ريال. وعندنا عرض "جرب أول مرة" خصم ١٥٪ للعملاء الجدد.' },
        { from: 'customer', text: 'العرض الشهري يهمني. سجلني' },
        { from: 'agent', agent: 'latifa', text: 'تم! بأرسلك تفاصيل الباقة الشهرية بعد ما تجرب الخدمة بكرة. وبأضيفك لقائمة العروض الموسمية — عندنا عروض رمضان قريب.' },
      ],
      en: [
        { from: 'customer', text: 'Do you have monthly subscriptions? Might save more' },
        { from: 'agent', agent: 'latifa', text: "I'm Latifa from marketing! Yes, monthly package — 4 visits for 1,800 SAR instead of 2,200. Save 400 SAR. Plus we have a \"First Time\" 15% discount for new customers." },
        { from: 'customer', text: 'Monthly interests me. Sign me up' },
        { from: 'agent', agent: 'latifa', text: "Done! I'll send monthly package details after your service tomorrow. Also adding you to seasonal offers — Ramadan deals coming soon." },
      ],
    },
    car_dealership: {
      ar: [
        { from: 'customer', text: 'عندكم عروض تمويل أو خصومات؟' },
        { from: 'agent', agent: 'latifa', text: 'أنا لطيفة من التسويق! ايه عندنا حملة "صيف بلا دفعة" — ٠٪ دفعة أولى + تأمين شامل مجاني أول سنة. والحملة تنتهي خلال ١٠ أيام.' },
        { from: 'customer', text: 'ممتاز! وإذا حبيت أشوف موديلات ثانية؟' },
        { from: 'agent', agent: 'latifa', text: 'بأرسلك كتالوج كامل الموديلات مع الأسعار. وسجلتك في قائمة "أول من يعرف" — أي موديل جديد يوصل بأخبرك قبل الكل.' },
      ],
      en: [
        { from: 'customer', text: 'Any financing deals or discounts?' },
        { from: 'agent', agent: 'latifa', text: "I'm Latifa from marketing! Yes, our \"Summer Zero Down\" campaign — 0% down payment + free comprehensive insurance first year. Ends in 10 days." },
        { from: 'customer', text: 'Excellent! What if I want to see other models?' },
        { from: 'agent', agent: 'latifa', text: "I'll send the full model catalog with prices. Also added you to our \"First to Know\" list — any new model arrives, you'll hear about it before everyone." },
      ],
    },
    insurance: {
      ar: [
        { from: 'customer', text: 'عندكم خصم لو أمّنت أكثر من سيارة؟' },
        { from: 'agent', agent: 'latifa', text: 'أنا لطيفة من التسويق! ايه عندنا خصم عائلي — ١٠٪ على السيارة الثانية و١٥٪ على الثالثة. وعندنا حملة "جدد وادخر" — تجديد مبكر يوفرلك ٥٪ إضافية.' },
        { from: 'customer', text: 'حلو. عندي سيارتين ثانية بعد' },
        { from: 'agent', agent: 'latifa', text: 'ممتاز! بأجهزلك عرض شامل للسيارات الثلاث مع الخصم العائلي. بأرسله قبل موعدك بكرة عشان المستشار يراجعه معاك.' },
      ],
      en: [
        { from: 'customer', text: 'Any discount for insuring multiple cars?' },
        { from: 'agent', agent: 'latifa', text: "I'm Latifa from marketing! Yes, family discount — 10% on second car, 15% on third. Plus our \"Renew & Save\" campaign — early renewal saves an extra 5%." },
        { from: 'customer', text: 'Nice. I have two more cars actually' },
        { from: 'agent', agent: 'latifa', text: "Great! I'll prepare a combined quote for all three with the family discount. I'll send it before your appointment tomorrow so the consultant can review it with you." },
      ],
    },
    retail: {
      ar: [
        { from: 'customer', text: 'عندكم برنامج ولاء أو نقاط؟' },
        { from: 'agent', agent: 'latifa', text: 'أنا لطيفة من التسويق! ايه عندنا برنامج "VIP" — كل ١٠٠ ريال = ١٠ نقاط. ٥٠٠ نقطة تحصل قسيمة ٥٠ ريال. وطلبك هذا يعطيك ١٢٠ نقطة مباشرة!' },
        { from: 'customer', text: 'سجلني في البرنامج' },
        { from: 'agent', agent: 'latifa', text: 'تم! حسابك VIP جاهز. وبمناسبة التسجيل عندك ١٥٪ خصم على طلبك الجاي. بأرسلك الكود على الواتساب.' },
      ],
      en: [
        { from: 'customer', text: 'Do you have a loyalty program or points?' },
        { from: 'agent', agent: 'latifa', text: "I'm Latifa from marketing! Yes, our VIP program — every 100 SAR = 10 points. 500 points = 50 SAR voucher. This order gives you 120 points instantly!" },
        { from: 'customer', text: 'Sign me up' },
        { from: 'agent', agent: 'latifa', text: "Done! VIP account ready. Welcome bonus: 15% off your next order. I'll send the code on WhatsApp." },
      ],
    },
    furniture: {
      ar: [
        { from: 'customer', text: 'عندكم عروض على طقم كامل للصالة؟' },
        { from: 'agent', agent: 'latifa', text: 'أنا لطيفة من التسويق! ايه عندنا حملة "جهّز صالتك" — خصم ٢٥٪ على الطقم الكامل (كنبة + طاولة + إضاءة). وعندنا تقسيط ٦ أشهر بدون فوائد.' },
        { from: 'customer', text: 'حلو! يعني لو أخذت الكنبة مع طاولة يكون أوفر؟' },
        { from: 'agent', agent: 'latifa', text: 'بالضبط! بأجهزلك عرض الطقم الكامل مع الخصم وخيارات التقسيط. المستشار بيراجعه معاك بكرة بالمعرض.' },
      ],
      en: [
        { from: 'customer', text: 'Any deals on a full living room set?' },
        { from: 'agent', agent: 'latifa', text: "I'm Latifa from marketing! Yes, our \"Complete Your Living Room\" campaign — 25% off full sets (sofa + table + lighting). Plus 6-month interest-free installments." },
        { from: 'customer', text: 'So if I get the sofa with a table it would be cheaper?' },
        { from: 'agent', agent: 'latifa', text: "Exactly! I'll prepare the full set quote with the discount and installment options. The consultant will review it with you at the showroom tomorrow." },
      ],
    },
    fnb: {
      ar: [
        { from: 'customer', text: 'لو حبيت أسوي مناسبات بشكل منتظم عندكم خصم؟' },
        { from: 'agent', agent: 'latifa', text: 'أنا لطيفة من التسويق! ايه عندنا باقة "عميل مميز" — ٣ مناسبات أو أكثر بالسنة تحصل ١٥٪ خصم دائم + أولوية بالحجز.' },
        { from: 'customer', text: 'سجلني. عندنا تقريباً مناسبة كل شهرين' },
        { from: 'agent', agent: 'latifa', text: 'ممتاز! سجلتك كعميل مميز. خصم ١٥٪ مطبق من هالطلب. وبأرسلك قوائم المواسم الجديدة أول بأول.' },
      ],
      en: [
        { from: 'customer', text: 'If I do regular events do you offer a discount?' },
        { from: 'agent', agent: 'latifa', text: "I'm Latifa from marketing! Yes, our \"Premium Client\" package — 3+ events per year gets you a permanent 15% discount + priority booking." },
        { from: 'customer', text: 'Sign me up. We have events roughly every two months' },
        { from: 'agent', agent: 'latifa', text: "Excellent! Registered as premium client. 15% discount applied from this order. I'll send you new seasonal menus as they launch." },
      ],
    },
    physio: {
      ar: [
        { from: 'customer', text: 'عندكم باقات أو عروض للجلسات؟' },
        { from: 'agent', agent: 'latifa', text: 'أنا لطيفة من التسويق! ايه عندنا باقة "التعافي الكامل" — ٨ جلسات بسعر ٦ (وفر ٥٠٠ ريال). وحالياً عندنا عرض التقييم المجاني لأول مرة.' },
        { from: 'customer', text: 'الباقة تهمني بعد ما أشوف التقييم' },
        { from: 'agent', agent: 'latifa', text: 'أكيد! بعد التقييم بكرة بأرسلك تفاصيل الباقة مع خطة العلاج المقترحة. وبأضيفك لنشرة النصائح الصحية الأسبوعية.' },
      ],
      en: [
        { from: 'customer', text: 'Do you have session packages or offers?' },
        { from: 'agent', agent: 'latifa', text: "I'm Latifa from marketing! Yes, our \"Full Recovery\" package — 8 sessions for the price of 6 (save 500 SAR). Plus free first assessment currently." },
        { from: 'customer', text: 'Package interests me after the assessment' },
        { from: 'agent', agent: 'latifa', text: "Of course! After tomorrow's assessment, I'll send package details with the suggested treatment plan. Also adding you to our weekly health tips newsletter." },
      ],
    },
    derma: {
      ar: [
        { from: 'customer', text: 'الباقة ٤ جلسات كم توفر بالضبط؟' },
        { from: 'agent', agent: 'latifa', text: 'أنا لطيفة من التسويق! باقة ٤ جلسات Q-Switch: ٢,٥٦٠ بدل ٣,٢٠٠ (وفر ٦٤٠ ريال). وعندنا حملة "بشرتك أحلى" — استشارة مجانية + خصم ١٠٪ إضافي هالشهر.' },
        { from: 'customer', text: 'ممتاز! بأقرر بعد الاستشارة' },
        { from: 'agent', agent: 'latifa', text: 'أكيد! بعد استشارتك بكرة بأرسلك العرض المخصص مع الخصم. وبأضيفك لقائمة العروض الحصرية للعناية بالبشرة.' },
      ],
      en: [
        { from: 'customer', text: 'How much exactly does the 4-session package save?' },
        { from: 'agent', agent: 'latifa', text: "I'm Latifa from marketing! 4-session Q-Switch package: 2,560 instead of 3,200 SAR (save 640). Plus our \"Glow Up\" campaign — free consultation + extra 10% off this month." },
        { from: 'customer', text: "Great! I'll decide after the consultation" },
        { from: 'agent', agent: 'latifa', text: "Of course! After your consultation tomorrow, I'll send a personalized offer with the discount. Also adding you to our exclusive skincare offers list." },
      ],
    },
    saas: {
      ar: [
        { from: 'customer', text: 'فيه عروض على الالتزام السنوي أو على الحجم؟' },
        { from: 'agent', agent: 'latifa', text: 'أنا لطيفة من التسويق! نعم، الالتزام السنوي خصم ١٥٪ على Business و٢٠٪ على Enterprise + Settlement T+٠ مجاناً. وحالياً عندنا حملة "Open & Pay" — إعفاء كامل من رسوم تفعيل الجهاز للحسابات الجديدة هالشهر.' },
        { from: 'customer', text: 'ممتاز! ممكن أشوف تجارب مطاعم بحجم قريب من حجمنا؟' },
        { from: 'agent', agent: 'latifa', text: 'أكيد! بأرسلك ٣ case studies من سلاسل مطاعم بـ٨-١٥ فرع كيف رفعوا متوسط العملية ٢٢٪ بعد تفعيل Apple Pay + Tap-to-Pay. وبأضيفكم لنشرة "نبض التجار" الأسبوعية فيها رؤى مبيعات.' },
      ],
      en: [
        { from: 'customer', text: 'Any discounts on annual commitment or on volume?' },
        { from: 'agent', agent: 'latifa', text: "I'm Latifa from marketing! Yes — annual commit: 15% off Business, 20% off Enterprise + free T+0 settlement. We also have an \"Open & Pay\" campaign — full activation fee waiver for new accounts this month." },
        { from: 'customer', text: 'Great! Can I see case studies from restaurants similar to our size?' },
        { from: 'agent', agent: 'latifa', text: "Absolutely! I'll send 3 case studies from 8-15 branch restaurant chains showing how they lifted avg ticket size 22% after enabling Apple Pay + Tap-to-Pay. Also adding you to our weekly \"Merchant Pulse\" newsletter with sales insights." },
      ],
    },
  };

  const supportMsgs = {
    real_estate: {
      ar: [
        { from: 'customer', text: 'بس سؤال — صديقي اشترى منكم وتأخر التسليم. ايش الوضع عادةً؟' },
        { from: 'agent', agent: 'khalid', text: 'أنا خالد من فريق الدعم. أفهم قلقك تماماً. نسبة التسليم بالوقت عندنا ٩٤٪. إذا صديقك يبي يتابع حالته، يرسلي رقم العقد وأتابع معاه شخصياً.' },
        { from: 'agent', agent: 'khalid', text: 'وبالنسبة لوحدتك — بأتابع معاك خطوة بخطوة من التعاقد للاستلام. أي شي تحتاجه أنا هنا ٢٤/٧.' },
      ],
      en: [
        { from: 'customer', text: 'Quick question — my friend bought from you and delivery was delayed. Is that common?' },
        { from: 'agent', agent: 'khalid', text: "I'm Khalid from support. I understand your concern. Our on-time delivery rate is 94%. If your friend wants to track their case, send me the contract number and I'll follow up personally." },
        { from: 'agent', agent: 'khalid', text: "For your unit — I'll be with you step by step from contract to handover. Anything you need, I'm here 24/7." },
      ],
    },
    cleaning: {
      ar: [
        { from: 'customer', text: 'سؤال — لو الفريق ما عجبني الشغل ايش أسوي؟' },
        { from: 'agent', agent: 'khalid', text: 'أنا خالد من الدعم. عندنا ضمان رضا ١٠٠٪ — لو ما عجبك الشغل، نرسلك فريق ثاني نفس اليوم بدون أي تكلفة إضافية.' },
        { from: 'agent', agent: 'khalid', text: 'وبعد كل زيارة بأرسلك استبيان سريع. أي ملاحظة بنعالجها فوراً. راحتك أولويتنا.' },
      ],
      en: [
        { from: 'customer', text: "Question — what if I'm not satisfied with the work?" },
        { from: 'agent', agent: 'khalid', text: "I'm Khalid from support. We have a 100% satisfaction guarantee — if you're not happy, we send another team same day at no extra cost." },
        { from: 'agent', agent: 'khalid', text: "After every visit I'll send a quick survey. Any feedback gets addressed immediately. Your satisfaction is our priority." },
      ],
    },
    car_dealership: {
      ar: [
        { from: 'customer', text: 'طيب لو صار مشكلة بالسيارة بعد الشراء؟' },
        { from: 'agent', agent: 'khalid', text: 'أنا خالد من الدعم. السيارة عليها ضمان ٥ سنوات / ١٠٠ ألف كم. أي مشكلة — ترسلي على الواتساب وأحجزلك موعد صيانة خلال ٢٤ ساعة.' },
        { from: 'agent', agent: 'khalid', text: 'وعندنا خدمة السيارة البديلة — لو سيارتك تحتاج أكثر من يوم بالوكالة، نوفرلك سيارة بديلة مجاناً.' },
      ],
      en: [
        { from: 'customer', text: 'What if there are issues after purchase?' },
        { from: 'agent', agent: 'khalid', text: "I'm Khalid from support. 5-year / 100K km warranty. Any issue — message me on WhatsApp and I'll book maintenance within 24 hours." },
        { from: 'agent', agent: 'khalid', text: "We also have loaner car service — if your car needs more than a day at the dealer, we provide a free replacement." },
      ],
    },
    insurance: {
      ar: [
        { from: 'customer', text: 'لو صار حادث، كم ياخذ وقت المطالبة؟' },
        { from: 'agent', agent: 'khalid', text: 'أنا خالد من الدعم. المطالبات عندنا تُعالج خلال ٤٨ ساعة. ترسلي صور الحادث على الواتساب مباشرة وأنا أفتح لك ملف فوراً.' },
        { from: 'agent', agent: 'khalid', text: 'وعندنا خدمة المتابعة — بأرسلك تحديثات على حالة المطالبة أول بأول بدون ما تحتاج تتصل.' },
      ],
      en: [
        { from: 'customer', text: 'If an accident happens, how long does the claim take?' },
        { from: 'agent', agent: 'khalid', text: "I'm Khalid from support. Claims are processed within 48 hours. Send accident photos on WhatsApp and I'll open a file immediately." },
        { from: 'agent', agent: 'khalid', text: "We also have proactive tracking — I'll send you claim status updates automatically without you having to call." },
      ],
    },
    retail: {
      ar: [
        { from: 'customer', text: 'لو المنتج ما عجبني، ايش سياسة الإرجاع؟' },
        { from: 'agent', agent: 'khalid', text: 'أنا خالد من الدعم. عندنا إرجاع مجاني خلال ١٤ يوم — بأرسلك بوليصة الشحن. أو ممكن تبدله من الفرع مباشرة.' },
        { from: 'agent', agent: 'khalid', text: 'وبعد التوصيل بأتابع معاك — لو أي شي مو تمام، ترسلي وأرتبلك الحل خلال ساعات.' },
      ],
      en: [
        { from: 'customer', text: "What's the return policy if I don't like it?" },
        { from: 'agent', agent: 'khalid', text: "I'm Khalid from support. Free returns within 14 days — I'll send the shipping label. Or exchange directly at the store." },
        { from: 'agent', agent: 'khalid', text: "After delivery I'll follow up — if anything isn't right, message me and I'll arrange a solution within hours." },
      ],
    },
    furniture: {
      ar: [
        { from: 'customer', text: 'لو وصلت القطعة فيها عيب ايش أسوي؟' },
        { from: 'agent', agent: 'khalid', text: 'أنا خالد من الدعم. عندنا ضمان سنتين على كل القطع. لو فيه أي عيب — صوره وأرسله لي وبنبدلها خلال ٧٢ ساعة بدون أي تكلفة.' },
        { from: 'agent', agent: 'khalid', text: 'وفريق التركيب عندنا متخصص — لو أي ملاحظة على التركيب نرسلهم يعيدونه صح نفس اليوم.' },
      ],
      en: [
        { from: 'customer', text: 'What if the piece arrives damaged?' },
        { from: 'agent', agent: 'khalid', text: "I'm Khalid from support. 2-year warranty on all pieces. Any defect — photo it, send to me, and we replace within 72 hours at no cost." },
        { from: 'agent', agent: 'khalid', text: "Our installation team is specialized — any issue with setup, we send them back same day to fix it." },
      ],
    },
    fnb: {
      ar: [
        { from: 'customer', text: 'لو الأكل ما كان بالمستوى ايش تسوون؟' },
        { from: 'agent', agent: 'khalid', text: 'أنا خالد من الدعم. ضمان جودة ١٠٠٪. لو أي طبق ما عجبكم — نبدله فوراً. ولو التجربة ككل ما كانت مرضية، نرجعلك المبلغ كامل.' },
        { from: 'agent', agent: 'khalid', text: 'وشيف متخصص بيكون موجود وقت المناسبة يتأكد إن كل شي تمام. أي شي تحتاجه، أنا على الواتساب طول الوقت.' },
      ],
      en: [
        { from: 'customer', text: "What if the food quality isn't up to standard?" },
        { from: 'agent', agent: 'khalid', text: "I'm Khalid from support. 100% quality guarantee. Any dish not up to standard — we replace immediately. If the overall experience is unsatisfactory, full refund." },
        { from: 'agent', agent: 'khalid', text: "A dedicated chef will be on-site during the event to ensure everything is perfect. Anything you need, I'm on WhatsApp the whole time." },
      ],
    },
    physio: {
      ar: [
        { from: 'customer', text: 'لو ما حسيت بتحسن بعد الجلسات ايش يصير؟' },
        { from: 'agent', agent: 'khalid', text: 'أنا خالد من الدعم. الدكتور بيقيّم تقدمك كل جلسة. لو ما فيه تحسن بعد ٣ جلسات، نعدّل الخطة بدون تكلفة إضافية أو نحولك لمتخصص ثاني.' },
        { from: 'agent', agent: 'khalid', text: 'وبأتابع معاك بين الجلسات — أرسلك تمارين المنزل وأتأكد إنك ماشي على الخطة.' },
      ],
      en: [
        { from: 'customer', text: "What if I don't see improvement after sessions?" },
        { from: 'agent', agent: 'khalid', text: "I'm Khalid from support. The doctor assesses progress every session. If no improvement after 3 sessions, we adjust the plan at no extra cost or refer to another specialist." },
        { from: 'agent', agent: 'khalid', text: "I'll also follow up between sessions — sending home exercises and making sure you're on track." },
      ],
    },
    derma: {
      ar: [
        { from: 'customer', text: 'لو ما عجبتني النتيجة بعد الجلسات ايش الخيارات؟' },
        { from: 'agent', agent: 'khalid', text: 'أنا خالد من الدعم. الدكتورة بتصورلك قبل وبعد كل جلسة عشان تشوفين الفرق. لو النتيجة ما كانت مرضية، نقيّم ونبدّل البروتوكول بدون تكلفة.' },
        { from: 'agent', agent: 'khalid', text: 'وبأتابع معاك بعد كل جلسة — أي احمرار أو ملاحظة ترسلي صورة وأوصلك بالدكتورة فوراً.' },
      ],
      en: [
        { from: 'customer', text: "What if I'm not happy with results after sessions?" },
        { from: 'agent', agent: 'khalid', text: "I'm Khalid from support. The doctor takes before/after photos every session so you can see the difference. If results aren't satisfactory, we reassess and switch protocols at no cost." },
        { from: 'agent', agent: 'khalid', text: "I'll follow up after every session — any redness or concern, send me a photo and I'll connect you with the doctor immediately." },
      ],
    },
    saas: {
      ar: [
        { from: 'customer', text: 'لو انقطع جهاز POS أو تأخر Settlement، كم وقت الاستجابة؟' },
        { from: 'agent', agent: 'khalid', text: 'أنا خالد من الدعم. SLA: ١٥ دقيقة لانقطاع الأجهزة، ساعة لمشاكل Settlement، ٤ ساعات للعادية. Enterprise عنده مدير حساب مخصص + قناة دعم Slack وخط ساخن ٢٤/٧.' },
        { from: 'agent', agent: 'khalid', text: 'وعندنا Status Page شفاف وواي-فاي احتياطي تلقائي على كل جهاز — لو الـ4G انقطع الجهاز يحوّل تلقائياً. credits آلية لأي خرق SLA.' },
      ],
      en: [
        { from: 'customer', text: 'If a terminal goes offline or settlement is delayed, what\'s your response time?' },
        { from: 'agent', agent: 'khalid', text: "I'm Khalid from support. SLAs: 15 mins for terminal outages, 1h for settlement issues, 4h for normal. Enterprise gets a dedicated account manager + Slack support channel and a 24/7 hotline." },
        { from: 'agent', agent: 'khalid', text: "We also have a transparent Status Page and automatic Wi-Fi failover on every terminal — if 4G drops, the device switches automatically. Automatic credits for any SLA breach." },
      ],
    },
  };

  const baseMsgs = (allMsgs[industryId] || allMsgs.cleaning)[lang] || allMsgs.cleaning.en;
  const mktg = (marketingMsgs[industryId] || marketingMsgs.cleaning)[lang] || marketingMsgs.cleaning.en;
  const supp = (supportMsgs[industryId] || supportMsgs.cleaning)[lang] || supportMsgs.cleaning.en;

  // Insert marketing + support before the system message
  const systemIdx = baseMsgs.findIndex(m => m.from === 'system');
  if (systemIdx >= 0) {
    const before = baseMsgs.slice(0, systemIdx);
    const systemMsg = baseMsgs[systemIdx];
    return [...before, ...mktg, ...supp, systemMsg];
  }
  return [...baseMsgs, ...mktg, ...supp];
}

function getManagerMessages(industryId, lang, industry) {
  const a = industry.analytics || {};
  const sessions = a.sessions || 234;
  const conversions = a.conversions || 18;
  const bounceRate = a.bounceRate || 34;
  const cpa = a.cpa || 22;
  const responseTime = a.responseTime || '< 2 min';
  const resolutionRate = a.resolutionRate || 91;
  const pipelineValue = a.pipelineValue || '6.8M';
  const leadsToday = a.leadsToday || 4;
  const activeConvos = a.activeConversations || 12;
  const recAr = industry.recommendationAr || '';
  const recEn = industry.recommendationEn || '';
  const industryNameAr = industry.nameAr || 'أعمالك';
  const industryNameEn = industry.name || 'your business';

  // Ticket data from industry
  const tickets = industry.tickets || [];
  const openTickets = tickets.filter(t => t.status === 'open').length || 3;
  const totalTickets = tickets.length || 12;
  const negativeTickets = tickets.filter(t => t.sentiment === 'negative');
  const topComplaintAr = negativeTickets[0]?.issueAr || 'تأخير في الخدمة';
  const topComplaintEn = negativeTickets[0]?.issueEn || 'Service delay';
  const topComplainerAr = negativeTickets[0]?.customerAr || 'عميل';
  const topComplainerEn = negativeTickets[0]?.customerEn || 'Customer';

  // Industry-specific campaign names
  const campaignNames = {
    real_estate: { ar: ['حملة المشاريع الجديدة', 'حملة التمويل العقاري', 'عروض نهاية الشهر'], en: ['New Projects Campaign', 'Mortgage Offers', 'End of Month Deals'] },
    cleaning: { ar: ['حملة الاشتراكات', 'حملة رمضان للتنظيف', 'عروض التنظيف العميق'], en: ['Subscription Campaign', 'Ramadan Cleaning', 'Deep Clean Offers'] },
    car_dealership: { ar: ['حملة موديلات ٢٠٢٦', 'حملة التقسيط بدون دفعة', 'عروض نهاية الأسبوع'], en: ['2026 Models Campaign', 'Zero Down Payment', 'Weekend Offers'] },
    insurance: { ar: ['حملة التأمين الشامل', 'حملة تجديد الوثائق', 'عروض التأمين الصحي'], en: ['Comprehensive Insurance', 'Policy Renewal', 'Health Insurance Offers'] },
    retail: { ar: ['حملة استرجاع السلات', 'حملة العروض الموسمية', 'حملة العملاء الجدد'], en: ['Cart Recovery', 'Seasonal Offers', 'New Customer Welcome'] },
    furniture: { ar: ['حملة الأثاث الجديد', 'حملة تصميم المنازل', 'عروض التقسيط'], en: ['New Arrivals', 'Home Design', 'Financing Offers'] },
    fnb: { ar: ['حملة قائمة رمضان', 'حملة التموين', 'عروض التوصيل'], en: ['Ramadan Menu', 'Catering Campaign', 'Delivery Offers'] },
    physio: { ar: ['حملة متابعة المرضى', 'حملة الجلسات المنقطعة', 'عروض الباقات'], en: ['Patient Follow-up', 'Dropped Sessions', 'Package Offers'] },
    derma: { ar: ['حملة العناية بالبشرة', 'حملة متابعة الاستشارات', 'عروض الليزر'], en: ['Skincare Campaign', 'Consultation Follow-up', 'Laser Offers'] },
    saas: { ar: ['حملة تفعيل Apple Pay', 'استرجاع التجار غير النشطين', 'إعلان Tap-to-Pay على الآيفون'], en: ['Apple Pay Activation', 'Win-back Inactive Merchants', 'iPhone Tap-to-Pay Announcement'] },
  };
  const campaigns = campaignNames[industryId] || campaignNames.retail;

  // Industry-specific support patterns
  const supportPatterns = {
    real_estate: { ar: 'تأخير تسليم الوحدات', en: 'Unit delivery delays' },
    cleaning: { ar: 'تأخر وصول فريق التنظيف', en: 'Cleaning crew late arrivals' },
    car_dealership: { ar: 'تأخير توفر قطع الغيار', en: 'Spare parts availability delay' },
    insurance: { ar: 'بطء معالجة المطالبات', en: 'Slow claim processing' },
    retail: { ar: 'تأخر شحن الطلبات', en: 'Order shipping delays' },
    furniture: { ar: 'تأخير التوصيل والتركيب', en: 'Delivery and installation delays' },
    fnb: { ar: 'شكاوى جودة التوصيل', en: 'Delivery quality complaints' },
    physio: { ar: 'إلغاء مواعيد متكرر', en: 'Frequent appointment cancellations' },
    derma: { ar: 'نتائج غير مرضية بعد الجلسات', en: 'Unsatisfactory post-session results' },
    saas: { ar: 'انقطاع أجهزة POS من شريحة 4G', en: '4G SIM POS terminal outage' },
  };
  const pattern = supportPatterns[industryId] || supportPatterns.retail;

  // Industry-specific deal context
  const dealCtx = {
    real_estate: { ar: 'وحدات بقيمة عالية', en: 'high-value units', closedAr: '٣ وحدات (٤.٨ مليون)', closedEn: '3 units (4.8M SAR)' },
    cleaning: { ar: 'اشتراكات شهرية', en: 'monthly subscriptions', closedAr: '١٨ اشتراك (١٠,٨٠٠ ر.س/شهر)', closedEn: '18 subs (10,800 SAR/mo)' },
    car_dealership: { ar: 'صفقات سيارات', en: 'car deals', closedAr: '٤ سيارات (٥٨٠ ألف)', closedEn: '4 cars (580K SAR)' },
    insurance: { ar: 'وثائق تأمين', en: 'policies', closedAr: '٢٢ وثيقة (٨٩ ألف)', closedEn: '22 policies (89K SAR)' },
    retail: { ar: 'طلبات عالية', en: 'high-value orders', closedAr: '٤٥ طلب (٤٦ ألف)', closedEn: '45 orders (46K SAR)' },
    furniture: { ar: 'طلبات أثاث', en: 'furniture orders', closedAr: '٨ طلبات (١٣٥ ألف)', closedEn: '8 orders (135K SAR)' },
    fnb: { ar: 'طلبات تموين', en: 'catering orders', closedAr: '٣ عقود (٤٥ ألف)', closedEn: '3 contracts (45K SAR)' },
    physio: { ar: 'باقات علاج', en: 'treatment packages', closedAr: '١٢ باقة (١٢٠ ألف)', closedEn: '12 packages (120K SAR)' },
    derma: { ar: 'حزم علاجية', en: 'treatment bundles', closedAr: '١٥ حزمة (٢١ ألف)', closedEn: '15 bundles (21K SAR)' },
    saas: { ar: 'صفقات Enterprise POS', en: 'Enterprise POS deals', closedAr: '٣ صفقات (٧٠٧ ألف ARR)', closedEn: '3 deals (707K SAR ARR)' },
  };
  const deals = dealCtx[industryId] || dealCtx.retail;

  const msgs = {
    ar: [
      { from: 'agent', agent: 'abdullah', text: `مرحباً! أنا عايدة، مساعدتك الذكية لإدارة ${industryNameAr}. جاهزة أعطيك نظرة شاملة. من وين تبي نبدأ — المبيعات، التسويق، أو الدعم؟` },
      { from: 'customer', text: 'ابدأي بالتسويق' },
      { from: 'agent', agent: 'latifa', text: `أنا لطيفة، مسؤولة التسويق الذكي. إليك آخر المستجدات:\n\nGoogle Analytics اليوم:\n- الزيارات: ${sessions} جلسة (+١٢٪ عن أمس)\n- معدل التحويل: ${conversions}٪\n- معدل الارتداد: ${bounceRate}٪\n- تكلفة الاستحواذ: ${cpa} ريال/عميل\n- أكثر المصادر: واتساب (٦٨٪)، مباشر (١٨٪)، إعلانات (١٤٪)` },
      { from: 'agent', agent: 'latifa', text: `توصيتي: ${recAr}` },
      { from: 'customer', text: 'ايش وضع الحملات الحالية؟' },
      { from: 'agent', agent: 'latifa', text: `عندك ٣ حملات:\n\n✅ ${campaigns.ar[0]} — معدل فتح ٤٢٪، ${Math.round(leadsToday * 3)} رد\n⏱ ${campaigns.ar[1]} — مجدولة بكرة ٤ م (${Math.round(sessions * 0.07)} مستلم)\n📝 ${campaigns.ar[2]} — مسودة، بانتظار اعتماد Meta\n\n${campaigns.ar[0]} حققت أعلى أداء. تبي نكررها بنفس الشريحة؟` },
      { from: 'customer', text: 'طيب ايش وضع المبيعات؟' },
      { from: 'agent', agent: 'abdullah', text: `أنا عبدالله، مسؤول المبيعات. إليك الوضع:\n\nPipeline:\n- القيمة الإجمالية: ${pipelineValue}\n- ${deals.ar}: ${leadsToday + 8} صفقة مفتوحة\n- معدل الإغلاق: ${Math.round(conversions * 3.2)}٪\n- أُغلق هالأسبوع: ${deals.closedAr}\n- عملاء جدد اليوم: ${leadsToday}\n- محادثات نشطة: ${activeConvos}\n\n⚠️ ${Math.max(2, Math.round(leadsToday * 0.7))} عملاء ما تم المتابعة معهم خلال ٤٨ ساعة. تبيني أتواصل معهم؟` },
      { from: 'customer', text: 'ايه تابعهم. وايش وضع الدعم والشكاوى؟' },
      { from: 'agent', agent: 'khalid', text: `أنا خالد، مسؤول الدعم. التقرير:\n\nالتذاكر:\n- إجمالي اليوم: ${totalTickets} تذكرة\n- محلولة: ${totalTickets - openTickets} (${resolutionRate}٪)\n- مفتوحة: ${openTickets} (واحدة أولوية عالية)\n- متوسط وقت الحل: ${responseTime}\n\n⚠️ نمط مكتشف: ${pattern.ar} — ${topComplainerAr} قدّم شكوى بخصوص "${topComplaintAr}". هالمشكلة تتكرر.` },
      { from: 'agent', agent: 'khalid', text: `رضا العملاء:\n- ${Math.round(88 + resolutionRate * 0.05)}٪ من العملاء راضين عن الخدمة\n- ${Math.round(50 + resolutionRate * 0.2)}٪ يرشحونك لغيرهم\n- ${resolutionRate}٪ من الشكاوى تُحل من أول تواصل\n\nالوضع جيد ما عدا مشكلة ${pattern.ar}. تبيني أصعّد الموضوع؟` },
    ],
    en: [
      { from: 'agent', agent: 'abdullah', text: `Hi! I'm AIDA, your AI assistant for managing ${industryNameEn}. Ready to give you a full overview. Where should we start — Sales, Marketing, or Support?` },
      { from: 'customer', text: 'Start with marketing' },
      { from: 'agent', agent: 'latifa', text: `I'm Latifa, your AI marketing lead. Here's the latest:\n\nGoogle Analytics Today:\n- Sessions: ${sessions} (+12% vs yesterday)\n- Conversion Rate: ${conversions}%\n- Bounce Rate: ${bounceRate}%\n- Cost per Acquisition: ${cpa} SAR/customer\n- Top Sources: WhatsApp (68%), Direct (18%), Ads (14%)` },
      { from: 'agent', agent: 'latifa', text: `My recommendation: ${recEn}` },
      { from: 'customer', text: 'How are current campaigns doing?' },
      { from: 'agent', agent: 'latifa', text: `You have 3 campaigns:\n\n✅ ${campaigns.en[0]} — 42% open rate, ${Math.round(leadsToday * 3)} responses\n⏱ ${campaigns.en[1]} — scheduled tomorrow 4 PM (${Math.round(sessions * 0.07)} recipients)\n📝 ${campaigns.en[2]} — draft, awaiting Meta approval\n\n${campaigns.en[0]} had the best performance. Want to replicate for the same segment?` },
      { from: 'customer', text: "What about sales?" },
      { from: 'agent', agent: 'abdullah', text: `I'm Abdullah, your sales lead. Here's the status:\n\nPipeline:\n- Total Value: ${pipelineValue}\n- ${deals.en}: ${leadsToday + 8} open deals\n- Close Rate: ${Math.round(conversions * 3.2)}%\n- Closed This Week: ${deals.closedEn}\n- New Leads Today: ${leadsToday}\n- Active Conversations: ${activeConvos}\n\n⚠️ ${Math.max(2, Math.round(leadsToday * 0.7))} leads haven't been followed up in 48 hours. Want me to reach out?` },
      { from: 'customer', text: 'Yes follow up. What about support and complaints?' },
      { from: 'agent', agent: 'khalid', text: `I'm Khalid, support lead. Report:\n\nTickets:\n- Total Today: ${totalTickets}\n- Resolved: ${totalTickets - openTickets} (${resolutionRate}%)\n- Open: ${openTickets} (one high priority)\n- Avg Resolution Time: ${responseTime}\n\n⚠️ Pattern detected: ${pattern.en} — ${topComplainerEn} filed a complaint about "${topComplaintEn}". This is a recurring issue.` },
      { from: 'agent', agent: 'khalid', text: `Customer Satisfaction:\n- ${Math.round(88 + resolutionRate * 0.05)}% of customers are happy with the service\n- ${Math.round(50 + resolutionRate * 0.2)}% would recommend you to others\n- ${resolutionRate}% of complaints resolved on first contact\n\nOverall good except for ${pattern.en}. Want me to escalate?` },
    ],
  };
  return msgs[lang] || msgs.en;
}

export default function InteractiveDemo({ lang, t, industry, notify, onChangeIndustry, setDemoPhone }) {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('+966 ');
  const [name, setName] = useState('');
  const [extraPhone, setExtraPhone] = useState('');
  const [extraName, setExtraName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  const [timer, setTimer] = useState(0);
  const [showEndModal, setShowEndModal] = useState(false);
  const [campaignSent, setCampaignSent] = useState(false);
  const [customerStage, setCustomerStage] = useState(0);
  const [managerDept, setManagerDept] = useState(null);
  const chatEndRef = useRef(null);
  const timerRef = useRef(null);
  const msgTimerRef = useRef(null);

  const isRtl = lang === 'ar';

  useEffect(() => {
    if (step === 3) {
      timerRef.current = setInterval(() => setTimer(prev => prev + 1), 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [step]);

  // Play messages based on scenario — using interval + ref for idx
  const allMsgsRef = useRef([]);
  const msgIdxRef = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (step === 3 && scenario) {
      const mockMsgs = scenario === 'customer'
        ? getCustomerMessages(industry.id, lang)
        : getManagerMessages(industry.id, lang, industry);
      
      allMsgsRef.current = mockMsgs;
      msgIdxRef.current = 0;
      setMessages([]);

      // Use a fixed interval to advance messages
      intervalRef.current = setInterval(() => {
        const i = msgIdxRef.current;
        const msgs = allMsgsRef.current;
        if (i >= msgs.length) {
          clearInterval(intervalRef.current);
          return;
        }
        const msg = msgs[i];
        setMessages(prev => [...prev, { ...msg, time: new Date() }]);

        if (msg.agent === 'abdullah') { setCustomerStage(0); setManagerDept('sales'); }
        else if (msg.agent === 'latifa') { setCustomerStage(1); setManagerDept('marketing'); }
        else if (msg.agent === 'khalid') { setCustomerStage(2); setManagerDept('support'); }

        msgIdxRef.current = i + 1;
      }, 3000);
    }
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line
  }, [step, scenario]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const handleConnect = async () => {
    if (!phone || phone.length < 8 || !name) return;
    setIsLoading(true);
    try {
      setSessionId('demo-' + Date.now());
      if (setDemoPhone) setDemoPhone(phone.replace(/\s/g, ''));
      setStep(2);
    } catch (e) {
      notify(lang === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
    setIsLoading(false);
  };

  const handleSelectScenario = async (sc) => {
    clearInterval(intervalRef.current);
    clearTimeout(msgTimerRef.current);
    msgIdxRef.current = 0;
    setScenario(sc);
    setMessages([]);
    setTimer(0);
    setCampaignSent(false);
    setCustomerStage(0);
    setManagerDept(null);
    try {
      await triggerAgentMessage(phone, sc, industry.id, name, extraPhone, extraName);
    } catch (e) {
      console.log('Demo trigger queued (server may be offline)');
    }
    setStep(3);
  };

  const handleSendCampaign = async () => {
    setCampaignSent(true);
    await sendCampaignToNumber(phone);
    setCustomerStage(3);
    notify(lang === 'ar' ? 'تم إرسال الحملة التسويقية!' : 'Marketing campaign sent!');
  };

  const handleEndDemo = async () => {
    setShowEndModal(false);
    clearInterval(timerRef.current);
    clearTimeout(msgTimerRef.current);
    await blacklistNumber(phone, sessionId);
    if (extraPhone) await blacklistNumber(extraPhone, sessionId);
    setStep(4);
  };

  const handleRestart = () => {
    setStep(1);
    setPhone('+966 ');
    setName('');
    setExtraPhone('');
    setExtraName('');
    setSessionId(null);
    setScenario(null);
    setMessages([]);
    setTimer(0);
    setCampaignSent(false);
    setCustomerStage(0);
    setManagerDept(null);
    onChangeIndustry();
  };

  // ─── STEP 1: Number Collection (SKIPPED — go straight to step 2) ───
  // Phone input commented out per Sarah's request. Jump to scenario selection.

  // ─── STEP 1 & 2 MERGED: Scenario Selection with QR Codes ───
  if (step === 1 || step === 2) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: 'calc(100vh - 140px)', animation: 'fadeIn 0.4s ease'
      }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>{t.chooseExperience}</h2>
        <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 36 }}>
          {lang === 'ar' ? 'امسح الكود أو اضغط لتجربة العرض' : 'Scan the QR code or click to try the demo'}
        </p>

        <div style={{ display: 'flex', gap: 20, maxWidth: 720, width: '100%' }}>
          {[
            { id: 'customer', icon: IconUser, title: t.tryAsCustomer, desc: t.tryAsCustomerDesc, color: '#1B6B93', bg: '#EBF5FA', qr: `/qr/${industry.id}/customer.png`, fallback: '/qr/tourism/customer.png' },
            { id: 'manager', icon: IconBriefcase, title: t.tryAsManager, desc: t.tryAsManagerDesc, color: '#5B2D8E', bg: '#F7F2FC', qr: `/qr/${industry.id}/manager.png`, fallback: '/qr/tourism/manager.png' },
          ].map(sc => {
            const Icon = sc.icon;
            return (
              <div key={sc.id} onClick={() => handleSelectScenario(sc.id)}
                style={{
                  flex: 1, background: '#fff', borderRadius: 16, padding: '32px 28px',
                  border: '2px solid #E5E7EB', cursor: 'pointer', transition: 'all 0.3s', textAlign: 'center'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = sc.color; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 32px ${sc.color}22`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {/* QR Code */}
                <div style={{
                  width: 160, height: 160, borderRadius: 12, overflow: 'hidden',
                  margin: '0 auto 20px', border: `2px solid ${sc.color}33`, background: '#fff'
                }}>
                  <img src={sc.qr} alt={`QR ${sc.id}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { if (e.currentTarget.src !== window.location.origin + sc.fallback) e.currentTarget.src = sc.fallback; }} />
                </div>

                <div style={{
                  width: 48, height: 48, borderRadius: 12, background: sc.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'
                }}>
                  <Icon size={24} color={sc.color} stroke={1.5} />
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, color: '#1A1A2E' }}>{sc.title}</h3>
                <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.7, marginBottom: 16 }}>{sc.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── STEP 3: Live Monitor ───
  if (step === 3) {
    const customerStages = [
      { key: 'sales', label: lang === 'ar' ? 'المبيعات' : 'Sales', icon: IconChartArrowsVertical, color: '#1B6B93' },
      { key: 'marketing', label: lang === 'ar' ? 'التسويق' : 'Marketing', icon: IconSpeakerphone, color: '#B5651D' },
      { key: 'support', label: lang === 'ar' ? 'الدعم' : 'Support', icon: IconHeadset, color: '#7B2D5F' },
    ];

    const managerDepts = [
      { key: 'marketing', label: t.askingMarketing, color: '#B5651D', icon: IconSpeakerphone },
      { key: 'sales', label: t.askingSales, color: '#1B6B93', icon: IconChartArrowsVertical },
      { key: 'support', label: t.askingSupport, color: '#7B2D5F', icon: IconHeadset },
    ];

    return (
      <div style={{ height: 'calc(100vh - 104px)', display: 'flex', flexDirection: 'column', animation: 'fadeIn 0.3s ease' }}>
        <div style={{ flex: 1, display: 'flex', gap: 16, overflow: 'hidden' }}>
          {/* Panel A: Live conversation (60%) */}
          <div style={{
            flex: 3, background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB',
            display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}>
            <div style={{
              padding: '12px 20px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', animation: 'pulse 1.5s infinite' }} />
                <span style={{ fontWeight: 700, fontSize: 16 }}>{t.liveDemo}</span>
              </div>
              <span className="font-mono" style={{ padding: '4px 12px', borderRadius: 6, background: '#F3F4F6', fontSize: 14, fontWeight: 600, color: '#374151' }}>
                {formatTime(timer)}
              </span>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
              {messages.map((msg, i) => {
                if (msg.from === 'system') {
                  return (
                    <div key={i} style={{
                      margin: '12px auto', maxWidth: 420, padding: '10px 16px',
                      background: '#F7F2FC', border: '1px dashed #7B4FAE', borderRadius: 10,
                      fontSize: 12, color: '#5B2D8E', textAlign: 'center', whiteSpace: 'pre-line',
                      fontFamily: "'IBM Plex Mono', monospace", animation: 'slideUp 0.3s ease'
                    }}>
                      {msg.text}
                    </div>
                  );
                }
                const isCustomerMsg = msg.from === 'customer';
                const agent = msg.agent ? AGENTS[msg.agent] : null;
                const AIcon = msg.agent ? AGENT_ICONS[msg.agent] : null;
                return (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: isCustomerMsg ? (isRtl ? 'flex-start' : 'flex-end') : (isRtl ? 'flex-end' : 'flex-start'),
                    marginBottom: 10, animation: 'slideUp 0.3s ease'
                  }}>
                    <div style={{
                      maxWidth: '65%', padding: '10px 14px', borderRadius: 12,
                      background: isCustomerMsg ? '#DCF8C6' : '#fff',
                      border: isCustomerMsg ? 'none' : '1px solid #E5E7EB',
                      fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-line',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                    }}>
                      {agent && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          fontSize: 11, fontWeight: 600, color: agent.color, marginBottom: 4
                        }}>
                          {AIcon && <AIcon size={12} stroke={2} />}
                          {lang === 'ar' ? agent.nameAr : agent.name}
                        </div>
                      )}
                      {isCustomerMsg && (
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#16A34A', marginBottom: 4 }}>
                          {name}
                        </div>
                      )}
                      {msg.text}
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Panel B: Context (40%) */}
          <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'auto' }}>
            {/* Scenario + Info */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #E5E7EB' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                borderRadius: 8, fontSize: 14, fontWeight: 700,
                background: scenario === 'customer' ? '#EBF5FA' : '#F7F2FC',
                color: scenario === 'customer' ? '#1B6B93' : '#5B2D8E'
              }}>
                {scenario === 'customer' ? <IconUser size={18} /> : <IconBriefcase size={18} />}
                {scenario === 'customer' ? t.customerMode : t.managerMode}
              </div>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#6B7280' }}>{t.yourName}</span>
                  <span style={{ fontWeight: 600 }}>{name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#6B7280' }}>{t.yourPhone}</span>
                  <span className="font-mono" style={{ fontWeight: 600, direction: 'ltr' }}>{phone}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#6B7280' }}>{t.sessionTimer}</span>
                  <span className="font-mono" style={{ fontWeight: 600, color: '#5B2D8E' }}>{formatTime(timer)}</span>
                </div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                  borderRadius: 6, background: industry.color + '15', fontSize: 12,
                  fontWeight: 600, color: industry.color, width: 'fit-content'
                }}>
                  {lang === 'ar' ? industry.nameAr : industry.name}
                </div>
              </div>
            </div>

            {/* Progress / Department tracker */}
            {scenario === 'customer' ? (
              <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', marginBottom: 12 }}>
                  {lang === 'ar' ? 'الوكيل النشط' : 'Active Agent'}
                </div>
                {customerStages.map((stage, i) => {
                  const SIcon = stage.icon;
                  const active = customerStage === i;
                  const done = customerStage > i;
                  return (
                    <div key={stage.key} style={{
                      padding: '10px 14px', borderRadius: 8, marginBottom: 8,
                      background: active ? stage.color + '15' : done ? '#F0FDF4' : '#F8F9FA',
                      border: `1px solid ${active ? stage.color : done ? '#86EFAC' : '#E5E7EB'}`,
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 14, fontWeight: active ? 700 : 400,
                      color: active ? stage.color : done ? '#16A34A' : '#6B7280', transition: 'all 0.3s'
                    }}>
                      {done ? <IconCheck size={16} stroke={2} /> : <SIcon size={16} stroke={1.8} />}
                      {stage.label}
                      {active && <div style={{ marginInlineStart: 'auto', width: 8, height: 8, borderRadius: '50%', background: stage.color, animation: 'pulse 1.5s infinite' }} />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', marginBottom: 12 }}>
                  {lang === 'ar' ? 'القسم النشط' : 'Active Department'}
                </div>
                {managerDepts.map(dept => {
                  const DIcon = dept.icon;
                  const active = managerDept === dept.key;
                  return (
                    <div key={dept.key} style={{
                      padding: '10px 14px', borderRadius: 8, marginBottom: 8,
                      background: active ? dept.color + '15' : '#F8F9FA',
                      border: `1px solid ${active ? dept.color : '#E5E7EB'}`,
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 14, fontWeight: active ? 700 : 400,
                      color: active ? dept.color : '#6B7280', transition: 'all 0.3s'
                    }}>
                      <DIcon size={16} stroke={1.8} />
                      {dept.label}
                      {active && <div style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: '#22C55E', animation: 'pulse 1.5s infinite' }} />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 0', marginTop: 12, borderTop: '1px solid #E5E7EB'
        }}>
          <button onClick={() => setShowEndModal(true)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 10,
            background: '#EF4444', color: '#fff', border: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: 700, fontFamily: 'inherit'
          }}>
            <IconPlayerStop size={18} /> {t.endDemo}
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{
              padding: '10px 16px', borderRadius: 10, border: '1px solid #E5E7EB',
              background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: '#374151'
            }}>{t.extendDemo}</button>
            <button onClick={() => {
              clearInterval(intervalRef.current);
              clearTimeout(msgTimerRef.current);
              msgIdxRef.current = 0;
              setMessages([]);
              setTimer(0);
              setCampaignSent(false);
              setCustomerStage(0);
              setManagerDept(null);
              setScenario(scenario === 'customer' ? 'manager' : 'customer');
            }} style={{
              padding: '10px 16px', borderRadius: 10, border: '1px solid #E5E7EB',
              background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: '#374151'
            }}>{t.switchScenario}</button>
          </div>
        </div>

        {showEndModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: '28px', maxWidth: 400, width: '90%', animation: 'slideUp 0.3s ease', textAlign: 'center' }}>
              <IconPlayerStop size={48} color="#EF4444" stroke={1.5} style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 24, lineHeight: 1.6 }}>{t.endDemoConfirm}</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={() => setShowEndModal(false)} style={{
                  padding: '10px 24px', borderRadius: 10, border: '1px solid #E5E7EB',
                  background: '#fff', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit'
                }}>{t.cancel}</button>
                <button onClick={handleEndDemo} style={{
                  padding: '10px 24px', borderRadius: 10, border: 'none',
                  background: '#EF4444', color: '#fff', cursor: 'pointer', fontSize: 14,
                  fontWeight: 700, fontFamily: 'inherit'
                }}>{t.confirm}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── STEP 4: Demo Complete ───
  if (step === 4) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: 'calc(100vh - 140px)', animation: 'fadeIn 0.4s ease'
      }}>
        <div style={{
          background: '#fff', borderRadius: 20, padding: '48px', maxWidth: 520, width: '100%',
          border: '1px solid #E5E7EB', textAlign: 'center', boxShadow: '0 8px 32px rgba(91,45,142,0.08)'
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 20, background: '#DCFCE7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
          }}>
            <IconCheck size={36} color="#22C55E" stroke={2} />
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>{t.demoComplete}</h2>
          <p style={{ fontSize: 15, color: '#6B7280', marginBottom: 32 }}>{t.demoCompleteDesc}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
            <div style={{ padding: '16px', borderRadius: 12, background: '#F7F2FC', border: '1px solid #E5E7EB' }}>
              <div className="font-mono" style={{ fontSize: 28, fontWeight: 700, color: '#5B2D8E' }}>{formatTime(timer)}</div>
              <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>{t.sessionDuration}</div>
            </div>
            <div style={{ padding: '16px', borderRadius: 12, background: '#EBF5FA', border: '1px solid #E5E7EB' }}>
              <div className="font-mono" style={{ fontSize: 28, fontWeight: 700, color: '#1B6B93' }}>{messages.length}</div>
              <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>{t.messagesExchanged}</div>
            </div>
          </div>
          <button onClick={handleRestart} style={{
            padding: '14px 32px', borderRadius: 12, border: 'none',
            background: '#5B2D8E', color: '#fff', cursor: 'pointer',
            fontSize: 16, fontWeight: 700, fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: 8
          }}>
            <IconRefresh size={20} /> {t.startNewDemo}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
